import type { JSX } from '@builder.io/qwik';
import {
  useVisibleTask$,
  component$,
  useContext,
  useSignal,
} from '@builder.io/qwik';
import NavItem from '~/components/Layout/Header/Desktop/NavItem';
import SocialIcons from '~/components/Shared/Social/SocialIcons';
import NavDivider from '~/components/Layout/Header/Desktop/NavDivider';
import SecondaryLogo from '~/components/Layout/Header/Shared/SecondaryLogo';
import type { LinkValueInterface } from '~/types/AppData/Layout/LinkValueInterface';
import { NavContext } from '~/contexts/NavContext';
import { cva } from 'class-variance-authority';
import { AppDataContext } from '~/contexts/AppDataContext';
import { getCSSColumnGapFactor } from '~/utilities/getCSS';
import MegaMenu from '~/components/Layout/Header/Desktop/MegaMenu';

const wrapClasses = cva('flex h-full', {
  variants: {
    grow: {
      true: 'w-full',
      false: '',
    },
  },
});

const navClasses = cva('grow', {
  variants: {
    navInMobile: {
      true: 'block overflow-x-auto',
      false: 'hidden lg:block ',
    },
    maskImage: {
      si: '[mask-image:linear-gradient(90deg,white_95%,transparent)]',
    },
  },
});

const ulClasses = cva('flex items-center h-full w-full', {
  variants: {
    navInMobile: {
      true: 'whitespace-nowrap flex-nowrap',
      false: 'overflow-hidden flex-wrap',
    },
    columnGap: getCSSColumnGapFactor(),
    maskImage: {
      si: 'w-max mr-6',
    },
  },
});

export default component$((): JSX.Element => {
  const appData = useContext(AppDataContext);
  const {
    header,
    theme,
    hasAccessibility,
    navInMobile,
    grow = true,
    maskImage,
    canHaveMore = true,
  } = useContext(NavContext);
  const activeItem = useSignal('');
  const navRef = useSignal<Element>();
  const socialLinks = header.socialLinks;
  const secondaryLogo = header.secondaryLogo;
  const megaMenu = header.megaMenu;
  const navLinks = header.links?.value;
  const visibleLinks = useSignal<LinkValueInterface[] | undefined>(
    navLinks?.slice(0)
  ); // Initially all links visible
  const moreLinks = useSignal<LinkValueInterface[]>([]); // Links that overflow and will go into "More"

  // Load on page load
  // eslint-disable-next-line qwik/no-use-visible-task
  useVisibleTask$(() => {
    // We don't need to do this if we have a scrollable nav in mobile
    if (navInMobile && appData.payloadToSSR.isMobileViewer) return;

    // Don't do this if we can't have the "More" item
    if (!canHaveMore) return;

    // We don't want to do this if we're explicitly using a megaMenu
    if (megaMenu) return;

    const activateMore = () => {
      if (navRef.value && navLinks) {
        const ulElement = navRef.value.querySelector('ul');

        if (ulElement) {
          const navItems = Array.from(ulElement.children) as HTMLElement[];

          // Whether we already have a More item
          const hasMore = moreLinks.value.length;

          // Whether we need a More item
          let needMore = false;

          // The total width of all the subnav items
          let totalWidth = 0;

          // Last visible index is initially the last item
          let lastVisibleIndex = navItems.length;

          // Get the width of the <ul> (add 5 extra since decimals get rounded)
          const ulWidth = ulElement.getBoundingClientRect().width;
          const ulComputedStyles = window.getComputedStyle(ulElement);
          const ulGap = parseFloat(
            ulComputedStyles.getPropertyValue('column-gap')
          );

          // Compare the width of all the items to the width available
          navItems.forEach((item, index) => {
            let itemWidth = item.getBoundingClientRect().width;

            // Add the column gap to all item widths except the last item
            if (index < navItems.length - 1) {
              itemWidth += ulGap;
            }

            // Get the total width of all the <li>
            totalWidth += itemWidth;

            // If the totalWidth is greater than the ulWidth, then decrement lastVisibleIndex
            if (totalWidth > ulWidth) {
              needMore = true;
              lastVisibleIndex--;
            }
          });

          // And then subtract one more width so that we can add the More nav item
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
          if (needMore || hasMore) {
            lastVisibleIndex--;
          }

          // If we have room to add, we add back the index removed if hasMore is true above
          if (hasMore && ulWidth > totalWidth + 200) {
            lastVisibleIndex += 1;
          }

          // Then we also add another if the last link is "More" so we just show the link instead
          if (lastVisibleIndex === navLinks.length - 1) {
            lastVisibleIndex += 1;
          }

          const newMoreLinks = navLinks.slice(lastVisibleIndex);

          // We don't have to set these new values unless they're different
          if (newMoreLinks.length !== moreLinks.value.length) {
            // Update visible and "More" links based on wrapping
            visibleLinks.value = navLinks.slice(0, lastVisibleIndex);
            moreLinks.value = navLinks.slice(lastVisibleIndex);
          }
        }
      }
    };

    // Activate on page load
    activateMore();

    // Trigger on page resize
    window.addEventListener('resize', activateMore);

    // Cleanup the event listener when the component is destroyed
    return () => {
      window.removeEventListener('resize', activateMore);
    };
  });

  return (
    <div
      class={wrapClasses({
        grow,
      })}
    >
      {navLinks?.length && (
        <nav class={navClasses({ navInMobile, maskImage })} ref={navRef}>
          <ul
            class={ulClasses({
              navInMobile,
              maskImage,
              columnGap: theme.ColumnGap?.value as 0.75 | 1,
            })}
          >
            {visibleLinks.value?.map((each, index) => (
              <NavItem
                key={index}
                level={0}
                item={each}
                activeItem={activeItem}
              />
            ))}
            {moreLinks.value.length > 0 && canHaveMore && (
              <NavItem
                key='more'
                level={0}
                activeItem={activeItem}
                item={{
                  children: moreLinks.value,
                  link: {
                    text: header.moreLinksText?.value ?? 'More',
                    url: '-',
                  },
                }}
              />
            )}
            {megaMenu?.value && megaMenu.value.length > 0 && (
              <MegaMenu
                key='more'
                menu={megaMenu}
                iconColor={theme.LinkColorTypeNormal.value}
              />
            )}
          </ul>
        </nav>
      )}
      {(socialLinks || secondaryLogo || hasAccessibility) && (
        <div class='pl-10 md:pl-20 ml-auto flex items-center'>
          <div class='hidden lg:flex items-center h-full'>
            {socialLinks && (
              <SocialIcons
                icons={socialLinks.value}
                color={theme.SocialLinksColor.value}
                hoverColor={theme.SocialLinksHoverColor.value}
              />
            )}
            {hasAccessibility && <NavDivider />}
          </div>
          {secondaryLogo && <SecondaryLogo />}
        </div>
      )}
    </div>
  );
});
